import React, {
  ReactNode,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMutation } from '@apollo/client';
import * as R from 'ramda';

import { TASK_ASSETS_ADD } from '@atom/graph/task';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Checkbox, Collapse, Icon, List, Modal, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  ElementGroupsTreeItem,
  InventoryAssetTreeStateType,
} from '@atom/types/inventory';
import { Task, TaskAssetsAddInput } from '@atom/types/task';
import { WorkOrderDetailType } from '@atom/types/work';
import api from '@atom/utilities/api';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import iconUtilities from '@atom/utilities/iconUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

const { ListItem, ListItemText } = List;

type Tree = InventoryAssetTreeStateType | ElementGroupsTreeItem;

interface Props {
  open: boolean;
  onClose: () => void;
  workOrderDetail: WorkOrderDetailType;
  task: Task;
  refetch: () => void;
}

const styles = {
  modal: {
    height: '50vh',
    padding: 0,
  },
  progress: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggle: {
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  icon: {
    marginRight: '0.5rem',
  },
};

const getListItemStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level}rem`, paddingRight: '1rem' }
    : { paddingLeft: `${level + 2}rem`, paddingRight: '1rem' };

const isAsset = (tree: Tree): tree is InventoryAssetTreeStateType => {
  return tree.hasOwnProperty('assetType');
};

const AddWorkAssetsModal = (props: Props) => {
  const { schemas = [] } = useSchemas();

  const { onClose, workOrderDetail, task, refetch } = props;
  const { inventoryAssetId, inventoryAssetSchemaId } = props.workOrderDetail;

  const rootSchema = useMemo(() => {
    return schemas.find(({ id }) => id === inventoryAssetSchemaId);
  }, [schemas, inventoryAssetSchemaId]);

  const [rootTree, setRootTree] = useState<InventoryAssetTreeStateType>({});

  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set(),
  );

  const [addAsset, { loading: loadingAdd }] = useMutation<
    { taskAssetsAdd: string[] },
    { input: TaskAssetsAddInput }
  >(TASK_ASSETS_ADD);

  const resetState = () => {
    setOpen(new Set([inventoryAssetId]));
    setSelectedAssetIds(new Set());
  };

  useEffect(() => {
    const getInventoryAssetTree = async () => {
      setLoading(true);
      setOpen(new Set([inventoryAssetId]));

      const params = {
        includeAttributes: true,
        isVisibleAsSubtextAttributesOnly: true,
      };

      const { data } = await api.get<InventoryAssetTreeStateType>(
        `${INVENTORY_ASSETS_ENDPOINT}/${inventoryAssetId}/tree`,
        params,
      );

      setRootTree(data);
      setLoading(false);
    };

    if (props.open && !loading && rootTree.id !== inventoryAssetId) {
      getInventoryAssetTree();
    }
  }, [props.open, inventoryAssetId]);

  const disabledAssetIds = useMemo(() => {
    return new Set([...task.assetIds]);
  }, [task]);

  const handleToggle = (id: string) => {
    setOpen(toggleFromSet(open, id));
  };

  const handleCheck = (event: SyntheticEvent<any>, id: string) => {
    event.stopPropagation();
    setSelectedAssetIds(toggleFromSet(selectedAssetIds, id));
  };

  const handleSubmit = async () => {
    if (selectedAssetIds.size) {
      await addAsset({
        variables: {
          input: {
            workOrderId: workOrderDetail.id,
            taskId: task.id,
            assetIds: [...selectedAssetIds],
          },
        },
      });

      refetch();
    }

    onClose();
  };

  const buildSubText = (tree: any): ReactNode => {
    if (!isAsset(tree)) {
      return '';
    }

    const attributeIds = R.pathOr([], ['attributeGroups'], tree).reduce(
      (acc, attributeGroup) => {
        return [...acc, ...R.pathOr([], ['attributes'], attributeGroup)];
      },
      [],
    );

    const attributeSubtext = attributeIds.reduce(
      (acc: string, attributeId: string): any => {
        const attribute = tree.attributes[attributeId];
        if (!attribute.isVisibleAsSubtext) {
          return acc;
        }

        const label = attribute.name;
        const value = attributeDisplayUtility.display(
          attribute.value,
          attribute.dataType,
          attribute.unit,
        );

        const text = `${label}: ${value}`;
        return acc ? `${acc}, ${text}` : text;
      },
      '',
    );

    return isNilOrEmpty(attributeSubtext)
      ? tree.assetType
      : `${tree.assetType} \u00B7 ${attributeSubtext}`;
  };

  const renderList = (trees: Tree[], level: number) => {
    return trees.map((tree, index) => {
      const hasChildren = isAsset(tree)
        ? !!tree.elements?.length || !!tree.elementGroups?.length
        : !!tree.elements?.length;

      const subtext = buildSubText(tree);

      return (
        <React.Fragment key={index}>
          <ListItem
            disableGutters
            style={getListItemStyle(hasChildren, level)}
            onClick={() => (hasChildren ? handleToggle(tree.id) : () => {})}
          >
            {hasChildren && (
              <Icon style={styles.toggle}>
                {open.has(tree.id) ? 'arrow_drop_down' : 'arrow_right'}
              </Icon>
            )}
            {isAsset(tree) && (
              <Checkbox
                checked={
                  selectedAssetIds.has(tree.id) || disabledAssetIds.has(tree.id)
                }
                disabled={disabledAssetIds.has(tree.id)}
                onClick={event => handleCheck(event, tree.id)}
              />
            )}
            {!isAsset(tree) ? null : tree.id === inventoryAssetId ? (
              <div style={styles.icon}>
                {schemaUtilities.getSchemaIconFromSchemaOrAsset(rootSchema, {
                  imgOffset: '0',
                  iconSize: '34px',
                  marginTop: '0',
                })}
              </div>
            ) : (
              <Icon style={styles.icon} color={colors.brand.blue}>
                {iconUtilities.getDataManagementElementIcon(
                  tree.markerId,
                  true,
                )}
              </Icon>
            )}
            <ListItemText primary={tree.name} secondary={subtext} />
          </ListItem>
          {hasChildren && (
            <Collapse in={open.has(tree.id)} timeout="auto" unmountOnExit>
              {renderList(R.pathOr([], ['elements'], tree), level + 1)}
              {renderList(R.pathOr([], ['elementGroups'], tree), level + 1)}
            </Collapse>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Modal
      title="Add Assets"
      open={props.open}
      onCancel={onClose}
      onConfirm={handleSubmit}
      contentStyle={styles.modal}
      loading={loadingAdd}
      onExited={resetState}
      data-cy="addWorkInventoryModal"
    >
      {loading || !rootTree ? (
        <div style={styles.progress}>
          <Progress />
        </div>
      ) : (
        <>{renderList([rootTree], 1)}</>
      )}
    </Modal>
  );
};

export default AddWorkAssetsModal;
